import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import BpsGlobalLogo from '../Logo/BpsGlobal.jpeg';

// Helper function to load image as base64
const loadImageAsBase64 = (imagePath) => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'Anonymous';
    img.onload = () => {
      const canvas = document.createElement('canvas');
      const maxW = 100;
      const scale = img.width > maxW ? maxW / img.width : 1;
      canvas.width = img.width * scale;
      canvas.height = img.height * scale;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      resolve(canvas.toDataURL('image/jpeg', 0.7));
    };
    img.onerror = reject;
    img.src = imagePath;
  });
};

// Convert number to words
const numberToWords = (amount, currencyName = 'AED') => {
  const ones = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine',
    'Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen',
    'Eighteen', 'Nineteen'];
  const tens = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];

  const convert = (n) => {
    if (n < 20) return ones[n];
    if (n < 100) return tens[Math.floor(n / 10)] + (n % 10 ? ' ' + ones[n % 10] : '');
    if (n < 1000) return ones[Math.floor(n / 100)] + ' Hundred' + (n % 100 ? ' ' + convert(n % 100) : '');
    if (n < 100000) return convert(Math.floor(n / 1000)) + ' Thousand' + (n % 1000 ? ' ' + convert(n % 1000) : '');
    if (n < 10000000) return convert(Math.floor(n / 100000)) + ' Lakh' + (n % 100000 ? ' ' + convert(n % 100000) : '');
    return convert(Math.floor(n / 10000000)) + ' Crore' + (n % 10000000 ? ' ' + convert(n % 10000000) : '');
  };

  const num = Math.round(parseFloat(amount) * 100) / 100;
  const intPart = Math.floor(num);
  const decPart = Math.round((num - intPart) * 100);

  if (intPart === 0 && decPart === 0) return `${currencyName} Zero Only`;
  let words = `${currencyName} `;
  if (intPart > 0) words += convert(intPart);
  if (decPart > 0) words += (intPart > 0 ? ' and ' : '') + convert(decPart) + ' Cents';
  return words + ' Only';
};


export const generateModernUAEBill = async (saleData, companyDetails, items, banks = []) => {
  const doc = new jsPDF('p', 'mm', 'a4', true);
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 12;

  const currency = saleData.CurrencyName || 'AED';

  const bpsBlue = [0, 84, 166];
  const darkGray = [52, 58, 64];
  const lightGray = [245, 245, 245];
  const borderGray = [200, 200, 200];

  let yPos = 8;

  // ── HEADER ──────────────────────────────────────────────────────────────────
  // White background
  doc.setFillColor(255, 255, 255);
  doc.rect(0, 0, pageWidth, 48, 'F');

  // Logo (left)
  try {
    const logoBase64 = await loadImageAsBase64(BpsGlobalLogo);
    doc.addImage(logoBase64, 'JPEG', margin, yPos, 24, 24);
  } catch (e) {
    doc.setFontSize(10);
    doc.setTextColor(...bpsBlue);
    doc.setFont('helvetica', 'bold');
    doc.text('BPS', 18, 24);
  }

  // Company Name - next to logo
  const companyName = companyDetails.companyName || '';
  if (companyName) {
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...bpsBlue);
    doc.text(companyName, margin + 27, yPos + 10);
  }
  // Company info (right-aligned)
  const compName = companyDetails.companyName || 'BPS Global Trading LLC';
  const compAddr = companyDetails.address || 'Office 203, Bin Dasmal Building, Al Quoz Industrial First, Dubai, United Arab Emirates';
  const compTel = companyDetails.phone || '+971 50 6718052';
  const compEmail = companyDetails.email || 'sales@bpsglobaltrading.com';
  const compTRN = companyDetails.trn || companyDetails.vatNumber || '105445043000001';
  const compLicense = companyDetails.tradeLicense || '1589979';

  doc.setFontSize(12);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...bpsBlue);
  doc.text(compName, pageWidth - 12, 14, { align: 'right' });

  doc.setFontSize(7.5);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...darkGray);
  // Split long address across two lines if needed
  const addrLines = doc.splitTextToSize(compAddr, 120);
  doc.text(addrLines[0], pageWidth - 12, 20, { align: 'right' });
  if (addrLines[1]) doc.text(addrLines[1], pageWidth - 12, 24.5, { align: 'right' });

  const telLine = `Tel: ${compTel}   Email: ${compEmail}`;
  doc.text(telLine, pageWidth - 12, 29, { align: 'right' });
  doc.text(`Trade License No.: ${compLicense}  |  TRN: ${compTRN}`, pageWidth - 12, 33.5, { align: 'right' });

  // ── TAX INVOICE BANNER ───────────────────────────────────────────────────────
  const bannerY = 41;
  doc.setFillColor(...bpsBlue);
  doc.rect(12, bannerY, pageWidth - 24, 10, 'F');
  doc.setFontSize(13);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(255, 255, 255);
  doc.text('Tax Invoice', pageWidth / 2, bannerY + 7, { align: 'center' });


  // ── INFO BOX: Invoice Details (left) | Bill To (right) ─────────────────────
  const infoY = 54;
  const infoH = 44;
  const halfW = (pageWidth - 24) / 2;

  // Outer border
  doc.setDrawColor(...borderGray);
  doc.setLineWidth(0.3);
  doc.rect(12, infoY, pageWidth - 24, infoH, 'S');
  // Divider between two halves
  doc.line(12 + halfW, infoY, 12 + halfW, infoY + infoH);

  // --- Invoice Details (left column) ---
  const ldX = 15;
  let ldY = infoY + 6;
  doc.setFontSize(9);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...darkGray);
  doc.text('Invoice Details', ldX, ldY);
  ldY += 5;

  const billNo = saleData.BillPrefix && saleData.FinYear
    ? `${saleData.BillPrefix}/${saleData.BillNumber}/${saleData.FinYear}`
    : saleData.BillNumber || 'N/A';
  const billDate = saleData.BillDate ? new Date(saleData.BillDate).toLocaleDateString('en-GB') : 'N/A';
  const dueDate = saleData.DueDate ? new Date(saleData.DueDate).toLocaleDateString('en-GB')
    : saleData.BillDate ? (() => { const d = new Date(saleData.BillDate); d.setDate(d.getDate() + 30); return d.toLocaleDateString('en-GB'); })() : 'N/A';
  const payTerms = saleData.PaymentTerms || 'Net 30 Days';
  const lpoRef = saleData.ReferenceNumber || saleData.RefNumber || saleData.LPONumber || saleData.RefNo || '-';

  const infoRows = [
    ['Invoice No.:', billNo],
    ['Invoice Date:', billDate],
    ['Due Date:', dueDate],
    ['Payment Terms:', payTerms],
    ['LPO / Reference No.:', lpoRef],
    ['Place of Supply:', 'Dubai, UAE'],
  ];

  doc.setFontSize(8);
  infoRows.forEach(([label, value]) => {
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...darkGray);
    doc.text(label, ldX, ldY);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(80, 80, 80);
    doc.text(String(value), ldX + 38, ldY);
    ldY += 5;
  });

  // --- Bill To (right column) ---
  const rdX = 12 + halfW + 4;
  let rdY = infoY + 6;
  doc.setFontSize(9);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...darkGray);
  doc.text('Bill to:', rdX, rdY);
  rdY += 5;

  const billToRows = [
    ['Customer Name:', saleData.CustomerName || '-'],
    ['Address:', saleData.ShipToAddress1 || '-'],
    ['', saleData.ShipToAddress2 || ''],
    ['Customer TRN:', saleData.VATNumber || saleData.GSTNumber || saleData.ShipGSTNo || '-'],
    ['Contact Person:', saleData.ContactPerson || '-'],
    ['Tel / Email:', saleData.CustomerPhone || saleData.CustomerEmail || '-'],
  ];

  doc.setFontSize(8);
  billToRows.forEach(([label, value]) => {
    if (!label && !value) return;
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...darkGray);
    if (label) doc.text(label, rdX, rdY);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(80, 80, 80);
    doc.text(String(value).substring(0, 42), rdX + 28, rdY);
    rdY += 5;
  });


  // ── ITEMS TABLE ──────────────────────────────────────────────────────────────
  const tableStartY = infoY + infoH + 4;

  const tableData = (saleData.saleDetails || []).map((item, index) => {
    const itemName = items.find(i => i.Id === item.ItemId)?.Name || item.ItemName || 'Item';
    const qty = parseFloat(item.Quantity || 0);
    const unitPrice = parseFloat(item.Rate || 0);
    const taxable = parseFloat(item.TaxableAmount || qty * unitPrice || 0);
    const vat = parseFloat(item.VATAmount || 0);
    const total = parseFloat(item.TotalAmount || taxable + vat || 0);
    return [
      index + 1,
      itemName,
      item.HSNCode || '3923.90',
      qty % 1 === 0 ? qty.toFixed(0) : qty.toFixed(2),
      unitPrice.toFixed(2),
      taxable.toFixed(2),
      vat.toFixed(2),
      total.toFixed(2),
    ];
  });

  // Pad to at least 5 rows so the table looks like the template
  while (tableData.length < 5) {
    tableData.push([tableData.length + 1, '', '', '', '', '', '', '']);
  }

  autoTable(doc, {
    startY: tableStartY,
    head: [[
      'S.No',
      'Description of Goods',
      'HS / Product Code',
      'Qty',
      `Unit Price (${currency})`,
      `Taxable Amount (${currency})`,
      'VAT @5%',
      `Total incl. VAT (${currency})`,
    ]],
    body: tableData,
    theme: 'grid',
    headStyles: {
      fillColor: bpsBlue,
      textColor: [255, 255, 255],
      fontSize: 7.5,
      fontStyle: 'bold',
      halign: 'center',
      valign: 'middle',
      lineWidth: 0.1,
      lineColor: [255, 255, 255],
      minCellHeight: 10,
    },
    bodyStyles: {
      fontSize: 8,
      textColor: darkGray,
      lineWidth: 0.1,
      lineColor: borderGray,
      minCellHeight: 8,
    },
    columnStyles: {
      0: { halign: 'center', cellWidth: 10 },
      1: { halign: 'left', cellWidth: 52 },
      2: { halign: 'center', cellWidth: 22 },
      3: { halign: 'center', cellWidth: 12 },
      4: { halign: 'right', cellWidth: 22 },
      5: { halign: 'right', cellWidth: 24 },
      6: { halign: 'right', cellWidth: 20 },
      7: { halign: 'right', cellWidth: 24 },
    },
    alternateRowStyles: { fillColor: [250, 250, 252] },
    margin: { left: 12, right: 12 },
  });


  // ── SUMMARY (right-aligned, below table) ────────────────────────────────────
  const summaryY = doc.lastAutoTable.finalY + 3;
  const sumW = 80;
  const sumX = pageWidth - 12 - sumW;

  const subtotal = parseFloat(saleData.TaxableAmount || 0);
  const totalVAT = parseFloat(saleData.VATAmount || 0);
  const grandTotal = parseFloat(saleData.TotalAmount || 0);

  // Summary border box
  doc.setDrawColor(...borderGray);
  doc.setLineWidth(0.3);
  doc.rect(sumX, summaryY, sumW, 28, 'S');

  const sumLabelX = sumX + 4;
  const sumValX = sumX + sumW - 4;
  let sumRowY = summaryY + 6;

  doc.setFontSize(8.5);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...darkGray);

  doc.text('Subtotal (Taxable Amount):', sumLabelX, sumRowY);
  doc.text(subtotal.toFixed(2), sumValX, sumRowY, { align: 'right' });
  sumRowY += 6;

  doc.text('Total VAT @ 5%:', sumLabelX, sumRowY);
  doc.text(totalVAT.toFixed(2), sumValX, sumRowY, { align: 'right' });
  sumRowY += 6;

  // Grand Total row (blue background)
  doc.setFillColor(...bpsBlue);
  doc.rect(sumX, sumRowY - 4, sumW, 10, 'F');
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9);
  doc.setTextColor(255, 255, 255);
  doc.text(`Grand Total (${currency}):`, sumLabelX, sumRowY + 2);
  doc.text(grandTotal.toFixed(2), sumValX, sumRowY + 2, { align: 'right' });

  // Amount in words
  const wordsY = summaryY + 32;
  doc.setFontSize(8.5);
  doc.setFont('helvetica', 'bolditalic');
  doc.setTextColor(...darkGray);
  const amountWords = numberToWords(grandTotal, currency);
  doc.text(`Amount in Words: ${amountWords}`, 12, wordsY);


  // ── BANK DETAILS + AUTHORIZED SIGNATORY ─────────────────────────────────────
  const bankY = wordsY + 6;
  const bankH = 36;
  const bankHalfW = (pageWidth - 24) / 2;

  // Outer border
  doc.setDrawColor(...borderGray);
  doc.setLineWidth(0.3);
  doc.rect(12, bankY, pageWidth - 24, bankH, 'S');
  doc.line(12 + bankHalfW, bankY, 12 + bankHalfW, bankY + bankH);

  // Bank details - use primary bank from banks list
  const primaryBank = banks.find(b => b.IsPrimary === true) || banks[0] || {};
  const bkX = 15;
  let bkY = bankY + 6;
  doc.setFontSize(8.5);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...darkGray);
  doc.text('Bank Details for Payment', bkX, bkY);
  bkY += 5;

  const bankName = primaryBank.Name || companyDetails.bankName || 'Mashreq Bank, Dubai';
  const accountName = primaryBank.AccountName || companyDetails.accountName || companyDetails.companyName || 'BPS Global Trading LLC';
  const accountNo = primaryBank.AccountNumber || companyDetails.accountNo || '';
  const iban = primaryBank.IFSCCode || companyDetails.iban || '';
  const swift = primaryBank.SwiftCode || companyDetails.swiftCode || 'BOMLAEAD';

  const bankRows = [
    ['Bank Name:', bankName],
    ['Account Name:', accountName],
    ['Account No.:', accountNo],
    ['IBAN:', iban],
    ['Swift Code:', swift],
  ];

  doc.setFontSize(8);
  bankRows.forEach(([label, value]) => {
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...darkGray);
    doc.text(label, bkX, bkY);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(80, 80, 80);
    doc.text(String(value), bkX + 30, bkY);
    bkY += 5;
  });

  // Authorized Signatory
  const sigX = 12 + bankHalfW + 4;
  let sigY = bankY + 6;
  doc.setFontSize(8.5);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...darkGray);
  doc.text('Authorized Signatory', sigX, sigY);
  sigY += 5;
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);
  doc.setTextColor(80, 80, 80);
  doc.text(`For ${companyDetails.companyName || 'BPS Global Trading LLC'}`, sigX, sigY);
  sigY += 16;
  // Signature line
  doc.setDrawColor(...borderGray);
  doc.setLineWidth(0.3);
  doc.line(sigX, sigY, sigX + bankHalfW - 10, sigY);
  sigY += 4;
  doc.setFontSize(7.5);
  doc.setFont('helvetica', 'italic');
  doc.setTextColor(130, 130, 130);
  doc.text('Signature & Company Stamp', sigX, sigY);


  // ── PAYMENT TERMS & CONDITIONS ───────────────────────────────────────────────
  const termsStartY = bankY + bankH + 5;

  // Use dynamic terms from modal selection (like PO PDF), fallback to hardcoded if none selected
  const dynamicTerms = saleData.SelectedTermsData || [];
  const hasSelectedTerms = dynamicTerms.length > 0;

  doc.setFontSize(8.5);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...darkGray);
  doc.text('Payment Terms & Conditions', 12, termsStartY);

  doc.setFontSize(6.8);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(80, 80, 80);

  let tY = termsStartY + 5;

  if (hasSelectedTerms) {
    // Render dynamically selected terms from the modal (like PO PDF)
    dynamicTerms.forEach((term, idx) => {
      if (tY > 280) { doc.addPage(); tY = 15; }
      const termText = term.Name || term.TermName || '';
      const wrapped = doc.splitTextToSize(`${idx + 1}. ${termText}`, pageWidth - 24);
      doc.text(wrapped, 12, tY);
      tY += wrapped.length * 3.8;
    });
  } else {
    // Fallback: hardcoded terms when no terms selected in modal
    const conditions = [
      'Payment is due within the period stated under Payment Terms above, calculated from the Invoice Date, unless otherwise agreed in writing between BPS Global Trading LLC and the Customer.',
      'Payments shall be made in AED, free of any deduction, set-off, or bank charges, by bank transfer to the account specified above, unless an alternate method is agreed in advance.',
      'Goods sold remain the property of BPS Global Trading LLC until payment has been received in full, notwithstanding delivery to the Customer.',
      'Any discrepancy in quantity, quality, or invoiced amount must be notified in writing to BPS Global Trading LLC within 7 calendar days of the invoice/delivery date.',
      'The invoice shall be deemed accepted if no response received in the above mentioned 7 days period.',
      'Goods once sold are not returnable or exchangeable except in accordance with a separate written agreement or warranty terms provided at the time of sale.',
      'This is a Tax Invoice issued pursuant to Federal Decree-Law No. (8) of 2017 on Value Added Tax (as amended) and its Executive Regulations.',
      'VAT is charged at 5% on the taxable value of goods/services supplied, unless the supply is zero-rated or exempt under the Decree-Law.',
      'This invoice, together with the Customer\'s purchase order/LPO (if any), constitutes the entire agreement between the parties in respect of the goods/services described herein.',
      'Any dispute arising out of or in connection with this invoice shall be governed by the laws of the United Arab Emirates and subject to the exclusive jurisdiction of the competent courts of Dubai.',
      'This document is system-generated and forms part of the official accounting records of BPS Global Trading LLC; it is valid without a wet signature issued through the Company\'s finance system.',
    ];

    conditions.forEach((line, idx) => {
      const wrapped = doc.splitTextToSize(`${idx + 1}. ${line}`, pageWidth - 24);
      doc.text(wrapped, 12, tY);
      tY += wrapped.length * 3.8;
    });
  }

  // ── FOOTER NOTE ──────────────────────────────────────────────────────────────
  tY += 3;
  doc.setDrawColor(...borderGray);
  doc.setLineWidth(0.2);
  doc.line(12, tY, pageWidth - 12, tY);
  tY += 4;

  doc.setFontSize(6.8);
  doc.setFont('helvetica', 'italic');
  doc.setTextColor(100, 100, 100);
  const footerNote = `This invoice is issued in accordance with UAE Federal Decree-Law No. (8) of 2017 on Value Added Tax and its Executive Regulations. VAT is charged at the standard rate of 5% on the taxable supply. All amounts are stated in ${currency}.`;
  const footerLines = doc.splitTextToSize(footerNote, pageWidth - 24);
  doc.text(footerLines, pageWidth / 2, tY, { align: 'center' });
  tY += footerLines.length * 3.8 + 3;

  // Website
  doc.setFontSize(7.5);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...bpsBlue);
  doc.text('www.bpsglobaltrading.com', pageWidth / 2, tY, { align: 'center' });

  return doc;
};


export const downloadBillPDF = async (saleData, companyDetails, items, banks = []) => {
  const doc = await generateModernUAEBill(saleData, companyDetails, items, banks);
  const billNo = saleData.BillPrefix && saleData.FinYear
    ? `${saleData.BillPrefix}_${saleData.BillNumber}_${saleData.FinYear}`
    : `Invoice_${saleData.BillNumber}`;
  doc.save(`${billNo}.pdf`);
};

export const printBillPDF = async (saleData, companyDetails, items, banks = []) => {
  const doc = await generateModernUAEBill(saleData, companyDetails, items, banks);
  doc.autoPrint();
  const pdfBlob = doc.output('blob');
  const pdfUrl = URL.createObjectURL(pdfBlob);
  const printWindow = window.open(pdfUrl, '_blank');
  if (printWindow) {
    printWindow.onload = function () {
      setTimeout(() => { printWindow.print(); }, 250);
    };
    setTimeout(() => { URL.revokeObjectURL(pdfUrl); }, 10000);
  } else {
    const billNo = saleData.BillPrefix && saleData.FinYear
      ? `${saleData.BillPrefix}_${saleData.BillNumber}_${saleData.FinYear}`
      : `Invoice_${saleData.BillNumber}`;
    doc.save(`${billNo}.pdf`);
    URL.revokeObjectURL(pdfUrl);
  }
};

export const getBillPDFBlob = async (saleData, companyDetails, items, banks = []) => {
  const doc = await generateModernUAEBill(saleData, companyDetails, items, banks);
  return doc.output('blob');
};

export const shareViaWhatsApp = async (saleData, companyDetails, items, phoneNumber, banks = []) => {
  try {
    const doc = await generateModernUAEBill(saleData, companyDetails, items, banks);
    const pdfBlob = doc.output('blob');
    const billNo = saleData.BillPrefix && saleData.FinYear
      ? `${saleData.BillPrefix}/${saleData.BillNumber}/${saleData.FinYear}`
      : saleData.BillNumber;
    const message = `Hello ${saleData.CustomerName},\n\nYour invoice ${billNo} for ${saleData.CurrencyName || 'AED'} ${parseFloat(saleData.TotalAmount || 0).toFixed(2)} is ready.\n\nThank you for your business!`;

    if (navigator.share && navigator.canShare && navigator.canShare({ files: [new File([pdfBlob], 'invoice.pdf', { type: 'application/pdf' })] })) {
      const file = new File([pdfBlob], `Invoice_${billNo.replace(/\//g, '_')}.pdf`, { type: 'application/pdf' });
      await navigator.share({ title: `Invoice ${billNo}`, text: message, files: [file] });
      return { success: true, method: 'native' };
    } else {
      const encodedMessage = encodeURIComponent(message);
      const whatsappUrl = phoneNumber
        ? `https://wa.me/${phoneNumber.replace(/[^0-9]/g, '')}?text=${encodedMessage}`
        : `https://wa.me/?text=${encodedMessage}`;
      const billFileName = `Invoice_${billNo.replace(/\//g, '_')}.pdf`;
      const url = URL.createObjectURL(pdfBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = billFileName;
      link.click();
      URL.revokeObjectURL(url);
      window.open(whatsappUrl, '_blank');
      return { success: true, method: 'fallback', message: 'PDF downloaded. Please attach it manually in WhatsApp.' };
    }
  } catch (error) {
    console.error('Error sharing via WhatsApp:', error);
    return { success: false, error: error.message };
  }
};


/**
 * Send the invoice PDF via Email using EmailJS.
 *
 * EmailJS template variables needed:
 *   {{to_email}}, {{to_name}}, {{from_name}}, {{subject}}, {{message}},
 *   {{invoice_number}}, {{invoice_date}}, {{invoice_amount}},
 *   {{invoice_pdf}} (Variable Attachment — full base64 data URI),
 *   {{pdf_name}}
 */
export const shareViaEmail = async (saleData, companyDetails, items, toEmail, emailjsConfig, banks = []) => {
  try {
    const doc = await generateModernUAEBill(saleData, companyDetails, items, banks);
    const billNo = saleData.BillPrefix && saleData.FinYear
      ? `${saleData.BillPrefix}/${saleData.BillNumber}/${saleData.FinYear}`
      : saleData.BillNumber;
    const fileName = `Invoice_${billNo.replace(/\//g, '_')}.pdf`;
    const subject = `Invoice ${billNo} from ${companyDetails.companyName || 'BPS Global Trading'}`;
    const messageBody =
      `Dear ${saleData.CustomerName || 'Customer'},\n\n` +
      `Please find attached your invoice ${billNo} ` +
      `dated ${saleData.BillDate ? new Date(saleData.BillDate).toLocaleDateString('en-GB') : ''} ` +
      `for ${saleData.CurrencyName || 'AED'} ${parseFloat(saleData.TotalAmount || 0).toFixed(2)}.\n\n` +
      `Thank you for your business!\n\n` +
      `Regards,\n${companyDetails.companyName || 'BPS Global Trading'}\n` +
      `${companyDetails.phone || ''}\n${companyDetails.email || ''}`;

    const { serviceId, templateId, publicKey } = emailjsConfig || {};
    if (!serviceId || !templateId || !publicKey || serviceId === 'YOUR_SERVICE_ID') {
      return {
        success: false,
        method: 'not_configured',
        error: 'Email service is not configured. Please set up EmailJS credentials in apiConfig.js.',
      };
    }

    const base64DataUri = doc.output('datauristring');
    const { default: emailjs } = await import('@emailjs/browser');

    const templateParams = {
      to_email: toEmail,
      to_name: saleData.CustomerName || 'Customer',
      from_name: companyDetails.companyName || 'BPS Global Trading',
      subject,
      message: messageBody,
      invoice_number: billNo,
      invoice_date: saleData.BillDate ? new Date(saleData.BillDate).toLocaleDateString('en-GB') : '',
      invoice_amount: `${saleData.CurrencyName || 'AED'} ${parseFloat(saleData.TotalAmount || 0).toFixed(2)}`,
      invoice_pdf: base64DataUri,
      pdf_name: fileName,
    };

    await emailjs.send(serviceId, templateId, templateParams, { publicKey });
    return { success: true, method: 'emailjs' };
  } catch (error) {
    console.error('Error sharing via Email:', error);
    const msg = error?.text || error?.message || 'Failed to send email.';
    return { success: false, error: msg };
  }
};
